import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPalette, 
    faWandMagicSparkles, 
    faChevronDown, 
    faCheck
} from '@fortawesome/free-solid-svg-icons';

const Dropdown = ({ title, icon, options = {}, currentSelection, onSelect, themeObj, showFixedTitle = false }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const isLight = themeObj?.isIconDark;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const optionKeys = options ? Object.keys(options) : [];

    return (
        <div className="relative hidden md:block" ref={dropdownRef}>
            {/* Trigger Pill Button */}
            <button
                type="button"
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono border backdrop-blur-md transition-all duration-200 focus:outline-none ${
                    isLight
                        ? 'bg-black/[0.04] border-black/15 text-black hover:bg-black/[0.08]'
                        : 'bg-white/[0.05] border-white/15 text-white hover:bg-white/[0.1]'
                }`}
            >
                {icon && <FontAwesomeIcon icon={icon} className="text-[10px] opacity-70" />}
                
                <span className="capitalize">
                    {showFixedTitle ? title : (currentSelection || title)}
                </span>

                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-[9px] opacity-60 transition-transform duration-200 ${
                        dropdownOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Animated Frosted Dropdown Menu */}
            <AnimatePresence>
                {dropdownOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className={`absolute right-0 mt-2 min-w-[140px] p-1.5 rounded-2xl border shadow-2xl backdrop-blur-2xl z-50 overflow-hidden ${
                            isLight
                                ? 'bg-white/95 border-black/10 shadow-black/10'
                                : 'bg-neutral-900/95 border-white/15 shadow-black/60'
                        }`}
                    >
                        <ul className="list-none m-0 p-0 space-y-0.5" role="listbox">
                            {optionKeys.map((optionName) => {
                                const isSelected = currentSelection?.toLowerCase() === optionName.toLowerCase();
                                return (
                                    <li
                                        key={optionName}
                                        role="option"
                                        aria-selected={isSelected}
                                        onClick={() => {
                                            onSelect(optionName);
                                            setDropdownOpen(false);
                                        }}
                                        className={`flex items-center justify-between px-3 py-1.5 rounded-xl text-xs font-mono capitalize cursor-pointer transition-colors ${
                                            isSelected
                                                ? isLight
                                                    ? 'bg-black/10 font-bold text-black'
                                                    : 'bg-white/15 font-bold text-white'
                                                : isLight
                                                    ? 'text-black/70 hover:bg-black/5 hover:text-black'
                                                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                                        }`}
                                    >
                                        <span>{optionName}</span>
                                        {isSelected && (
                                            <FontAwesomeIcon 
                                                icon={faCheck} 
                                                className="text-[10px] text-emerald-400 ml-2" 
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const ThemeSwitcher = ({ themes, theme, changeTheme, particleOptions, particleOption }) => {
    return (
        <div className="flex items-center space-x-2">
            {/* Theme Dropdown: now permanently displays "Themes" */}
            <Dropdown
                title="Themes"
                icon={faPalette}
                options={themes}
                currentSelection={theme}
                onSelect={(themeName) => changeTheme(themeName, null)}
                themeObj={themes?.[theme]}
                showFixedTitle={true}
            />

            {/* Particles Dropdown: permanently displays "Particles" */}
            {particleOptions && (
                <Dropdown
                    title="Particles"
                    icon={faWandMagicSparkles}
                    options={particleOptions}
                    currentSelection={particleOption}
                    onSelect={(optionName) => changeTheme(null, optionName)}
                    themeObj={themes?.[theme]}
                    showFixedTitle={true}
                />
            )}
        </div>
    );
};

export default ThemeSwitcher;